import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Building2, CheckCircle, AlertCircle, Clock } from 'lucide-react'

interface InvitationDetails {
  id: string
  team_name: string
  inviter_name: string
  role: string
  contact: string
  contact_type: 'email' | 'phone'
  expires_at: number
}

export default function InvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accepting, setAccepting] = useState(false)
  const [accepted, setAccepted] = useState(false)

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link')
      setLoading(false)
      return
    }

    fetchInvitationDetails()
  }, [token])

  const fetchInvitationDetails = async () => {
    if (!token) return

    try {
      setLoading(true)
      setError(null)

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/invitations/${token}`
      )

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('This invitation is invalid or has expired')
        }
        throw new Error('Failed to load invitation details')
      }

      const data = await response.json()
      setInvitation(data.invitation)
    } catch (err) {
      console.error('Failed to fetch invitation:', err)
      setError(err instanceof Error ? err.message : 'Failed to load invitation')
    } finally {
      setLoading(false)
    }
  }

  const handleAcceptInvitation = async () => {
    if (!token || !user) return

    setAccepting(true)
    setError(null)

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/invitations/${token}/accept`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to accept invitation')
      }

      setAccepted(true)
      
      // Redirect after a delay
      setTimeout(() => {
        navigate('/dashboard?invitation_accepted=true')
        window.location.reload() // Refresh to update company context
      }, 3000)
    } catch (err) {
      console.error('Failed to accept invitation:', err)
      setError(err instanceof Error ? err.message : 'Failed to accept invitation')
    } finally {
      setAccepting(false)
    }
  }

  const formatExpiration = (timestamp: number) => {
    const date = new Date(timestamp * 1000)
    const now = new Date()
    
    if (date < now) {
      return 'Expired'
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const isExpired = invitation && invitation.expires_at < Date.now() / 1000

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Invalid Invitation
            </h2>
            
            <p className="text-gray-600 mb-6">
              {error}
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/login')}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Go to Login
              </button>
              
              <button
                onClick={() => navigate('/')}
                className="w-full px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Sign in to accept invitation
            </h2>
            
            <p className="text-gray-600 mb-6">
              You need to sign in to accept this invitation to join{' '}
              <strong>{invitation?.team_name}</strong>.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate(`/invite/${token}/login`)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Continue to Sign In
              </button>
              
              <p className="text-sm text-gray-500 text-center">
                New users will be automatically registered during sign in
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome to {invitation?.team_name}!
            </h2>
            
            <p className="text-gray-600 mb-6">
              You've successfully joined the team as a{' '}
              <span className="font-medium">{invitation?.role.toLowerCase()}</span>.
              You'll be redirected to your dashboard shortly.
            </p>
            
            <div className="animate-pulse flex items-center justify-center text-sm text-gray-500">
              <div className="w-4 h-4 bg-blue-600 rounded-full mr-2 animate-bounce"></div>
              Redirecting to dashboard...
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isExpired) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Invitation Expired
            </h2>
            
            <p className="text-gray-600 mb-6">
              This invitation to join <strong>{invitation?.team_name}</strong> has expired.
              Please contact {invitation?.inviter_name} for a new invitation.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              You're invited!
            </h2>
            
            <p className="text-gray-600">
              {invitation?.inviter_name} has invited you to join their team
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Invitation Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Company:</span>
                <span className="font-medium">{invitation?.team_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Role:</span>
                <span className="font-medium">{invitation?.role}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Invited by:</span>
                <span className="font-medium">{invitation?.inviter_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Expires:</span>
                <span className="font-medium">
                  {invitation && formatExpiration(invitation.expires_at)}
                </span>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleAcceptInvitation}
              disabled={accepting}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
            >
              {accepting ? 'Accepting...' : 'Accept Invitation'}
            </button>
            
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}